import type { LocationSuggestion } from '@ixigo/cab-core';
import fetch from '@ixigo/fetch';
import type { CabSelectionOptions } from '@/components/cab-selection';
import type { SearchFormData } from '@/components/search-form';
import { executeApiCall } from './helper';
import type {
	ApiErrorResponse,
	BaseContactDetail,
	BaseFarePolicyDetail,
	RideDetails,
} from './types';

export interface TripDetails {
	rideDetails: RideDetails;
	cabSelection: CabSelectionOptions;
}

export interface FareDetailSuccessResponse {
	data: {
		tripDetails: TripDetails;
		farePolicies: BaseFarePolicyDetail;
		contactDetails?: BaseContactDetail;
	};
}

export type FareDetailErrorType =
	| 'NOT_SERVICEABLE'
	| 'AIRPORT_ONLY'
	| 'NO_CABS'
	| 'GENERIC_ERROR';
export type FareDetailsErrorResponse = ApiErrorResponse<FareDetailErrorType>;

export type FareDetailsResponse =
	| FareDetailSuccessResponse
	| FareDetailsErrorResponse;

interface LocationDetail {
	placeId: LocationSuggestion['placeId'];
	latitude: LocationSuggestion['lat'];
	longitude: LocationSuggestion['lng'];
}

export interface FareDetailsRequest {
	pickup: LocationDetail;
	drop: LocationDetail;
	pickupTime: string;
}

export const buildFareDetailsRequest = (
	searchFormData: Required<SearchFormData>,
): FareDetailsRequest => {
	// Convert ISO string to local time without timezone info
	const date = new Date(searchFormData.dateTime);
	const offset = date.getTimezoneOffset() * 60000; // offset in milliseconds
	const localDate = new Date(date.getTime() - offset);
	const localISOString = localDate.toISOString().slice(0, -1); // Remove 'Z'

	return {
		pickup: {
			latitude: searchFormData.pickup.lat,
			longitude: searchFormData.pickup.lng,
			placeId: searchFormData.pickup.placeId,
		},
		drop: {
			latitude: searchFormData.drop.lat,
			longitude: searchFormData.drop.lng,
			placeId: searchFormData.drop.placeId,
		},
		pickupTime: localISOString,
	};
};

export const fetchFareDetails = async (
	searchFormData: Required<SearchFormData>,
	abortController?: AbortSignal,
): Promise<FareDetailsResponse> => {
	const request = buildFareDetailsRequest(searchFormData);
	return executeApiCall(
		() =>
			fetch
				.post<FareDetailsResponse>('cabs/api/v1/fareDetails', {
					body: JSON.stringify(request),
					headers: {
						'Content-Type': 'application/json',
					},
					signal: abortController,
				})
				.json(),
		'fetchFareDetails',
	);
};
